import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["menu", "search", "item", "empty", "more"]
  static values = {
    searchUrl: String,
    pathTemplate: String,
    selectedId: Number
  }

  connect() {
    this.close = this.close.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.close)
    clearTimeout(this.searchTimeout)
    this.searchAbortController?.abort()
  }

  toggle(event) {
    event.stopPropagation()
    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.hide()
    }
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    document.addEventListener("click", this.close)
    if (this.hasSearchTarget) {
      requestAnimationFrame(() => this.searchTarget.focus())
    }
  }

  hide() {
    this.menuTarget.classList.add("hidden")
    document.removeEventListener("click", this.close)
    if (this.hasSearchTarget) {
      clearTimeout(this.searchTimeout)
      this.searchAbortController?.abort()
      this.searchTarget.value = ""
      this.removeRemoteItems()
      this.applyFilter("")
      this.toggleMoreNotice(true)
    }
  }

  close(event) {
    if (event && this.element.contains(event.target)) return
    this.hide()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()

    clearTimeout(this.searchTimeout)
    this.searchAbortController?.abort()
    this.removeRemoteItems()
    this.applyFilter(query, this.hasSearchUrlValue && query !== "")
    this.toggleMoreNotice(query === "")

    if (this.hasSearchUrlValue && query !== "") {
      this.searchTimeout = setTimeout(() => this.fetchMatches(query), 150)
    }
  }

  applyFilter(query, searching = false) {
    let visible = 0
    this.itemTargets.forEach((item) => {
      const haystack = item.dataset.search || ""
      const match = query === "" || haystack.includes(query)
      item.classList.toggle("hidden", !match)
      if (match) visible++
    })
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", searching || visible !== 0)
    }
  }

  async fetchMatches(query) {
    this.searchAbortController = new AbortController()

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      url.searchParams.set("query", query)
      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.searchAbortController.signal
      })
      if (!response.ok) throw new Error(`Pharmacy search failed: ${response.status}`)

      const pharmacies = await response.json()
      if (this.searchTarget.value.trim().toLowerCase() !== query) return

      this.addRemoteItems(pharmacies)
      this.applyFilter(query)
    } catch (error) {
      if (error.name === "AbortError") return
      if (this.searchTarget.value.trim().toLowerCase() !== query) return
      this.applyFilter(query)
    }
  }

  addRemoteItems(pharmacies) {
    const existingIds = new Set(this.itemTargets.map((item) => item.dataset.pharmacyId))

    pharmacies.forEach((pharmacy) => {
      if (existingIds.has(String(pharmacy.id))) return

      const item = document.createElement("li")
      item.dataset.pharmacySelectorTarget = "item"
      item.dataset.pharmacyId = pharmacy.id
      item.dataset.remote = "true"
      item.dataset.search = `${pharmacy.name} ${pharmacy.address}`.toLowerCase()

      const link = document.createElement("a")
      link.href = this.pathTemplateValue.replace("__pharmacy_id__", pharmacy.id)
      link.className = "block px-3 py-2 hover:bg-gray-50"
      if (this.hasSelectedIdValue && this.selectedIdValue === pharmacy.id) {
        link.classList.add("bg-indigo-50")
      }

      const name = document.createElement("div")
      name.className = "text-sm font-medium text-gray-900 truncate"
      name.textContent = pharmacy.name

      const address = document.createElement("div")
      address.className = "text-xs text-gray-500 truncate"
      address.textContent = pharmacy.address

      link.append(name, address)
      item.appendChild(link)
      this.emptyTarget.before(item)
    })
  }

  removeRemoteItems() {
    this.itemTargets
      .filter((item) => item.dataset.remote === "true")
      .forEach((item) => item.remove())
  }

  toggleMoreNotice(visible) {
    if (this.hasMoreTarget) this.moreTarget.classList.toggle("hidden", !visible)
  }
}
